// src/pages/types.ts —— 后台页定义契约（各页文件只声明 PageDef；表格 / 抽屉 / 表单 / 确认框机制归 table 域）。导出形状由契约锁冻结（TD-851）
import type { AdminPage } from '../../../shared/src/api/types';
import type { RouteKey } from '../../../shared/src/api/routes';
import type { PendingRouteKey } from '../../../shared/src/api/routes.pending';
import type { Dict, I18nPack } from '../../../shared/src/dc';
import type { AdminCtx } from '../domains/types';
import type { Module } from '../perm';
import type { Page } from '../state';
import type { BtnKind, CellVals, ChipKind, Tone } from './ui';

export interface ColDef {
  label: string; // t 键
  unit?: 'USDT'; // 表头单位后缀「 (USDT)」（佣金列，对客评审 2026-10-15 #26）；facet 下拉文案不带单位
  sortKey?: string; // C-00 sort 白名单列键
  facet?: string; // facet 参数名（静态绑定，06 附录 A-3）
  facetLabel?: (t: Dict, value: string) => string; // facet 选项文案（缺省显示服务端原值；未知值应回落原值）
}

export interface Need {
  module: Module;
  bit: 'edit' | 'approve';
}

// 逐行业务置灰（与权限位置灰同形：opacity .5、点击无动作、悬停 title）。权限位不足时以权限提示为准
export interface RowGate {
  disabled: boolean;
  title: string;
}

export interface ActionDef<R> {
  label: string; // t 键或已本地化文本（以 `~` 前缀表示文本）
  need?: Need;
  danger?: boolean;
  show?: (r: R) => boolean;
  gate?: (ctx: AdminCtx, r: R) => RowGate; // 06 C-08 S-04：一审人本人的「通过」预置灰
  run: (ctx: AdminCtx, r: R) => void | Promise<void>;
}

export interface DrawerAction {
  label: string;
  kind: BtnKind;
  need?: Need;
  gate?: (ctx: AdminCtx) => RowGate;
  run: (ctx: AdminCtx) => void | Promise<void>;
}

export interface DrawerSpec {
  kind: string;
  title: string;
  subtitle?: string;
  tabs?: { labels: readonly string[]; active: number; pick: (ctx: AdminCtx, i: number) => void };
  card?: { style: string; balance: string; brand: string; no: string };
  fields?: ReadonlyArray<{ k: string; v: string; wide?: boolean }>;
  list?: { title: string; rows: ReadonlyArray<{ left: string; sub?: string; right?: string; rightSub?: string; tone?: Tone; go?: (ctx: AdminCtx) => void }> };
  json?: string;
  jsonTitle?: string; // 缺省 t.rawPayload（0292 L311 偏差块，C-13「上游成本」）
  timeline?: ReadonlyArray<{ label: string; time: string }>;
  note?: boolean;
  actions: readonly DrawerAction[];
}

// file：图片上传控件（2026-10-16 处置凭证，0321 EV-1 偏差块）；值为表单自管的字符串（如已上传引用列表）
export type FieldType = 'text' | 'number' | 'password' | 'select' | 'switch' | 'readonly' | 'file';

export interface FieldDef {
  k: string;
  label: string;
  type: FieldType;
  options?: readonly string[];
  optionLabel?: (t: Dict, value: string) => string; // select 选项文案（缺省显示原值）
  wide?: boolean;
  optional?: boolean;
  placeholder?: string;
}

export type FormVals = Record<string, string | boolean>;

// 字段变更联动结果：vals 合并进当前表单值（可写不在 fields 中的隐藏键），err 非 undefined 时替换红条文案（'' = 清除）
export interface FormChange {
  vals?: FormVals;
  err?: string;
  // options：替换 select 字段的选项全集（键 = 字段 k），如按关键字远程搜索上级代理后的候选（多级代理 2026-10-18）
  options?: Readonly<Record<string, readonly string[]>>;
}

export interface FormDef {
  title: string;
  tip?: string;
  fields: readonly FieldDef[];
  init: FormVals;
  validate?: (v: FormVals, t: Dict) => string; // 返回非空 = 表单底部红条文案
  submit: (ctx: AdminCtx, v: FormVals) => Promise<void>; // 抛 ApiError → 红条显示 ui.err
  // 字段变更联动（2026-10-16 处置表单 UID 预览）：k = 刚改的字段，vals = 改后全量值。异步结果只在表单未换、未在提交、
  // 且触发字段值未再变化时合并（防旧响应覆盖新输入）
  onChange?: (ctx: AdminCtx, k: string, vals: FormVals) => FormChange | Promise<FormChange> | void;
  // file 字段选中文件（2026-10-16 处置凭证上传）：结果按 onChange 同口径合并（表单未换、未在提交时）
  onFiles?: (ctx: AdminCtx, k: string, files: readonly File[], vals: FormVals) => Promise<FormChange>;
}

export interface ConfirmSpec {
  title: string;
  text: string;
  label: string;
  run: (ctx: AdminCtx) => Promise<void>;
}

export interface StatVals {
  label: string;
  value: string;
  sub: string;
  dark?: boolean;
  danger?: boolean; // 数值红字（06 C-27：覆盖率 < 100%）
}

export type Source = { route: RouteKey; tabParam: 'tab' | 'status' | 'none'; tabFacet?: Readonly<Record<string, Readonly<Record<string, string>>>> } | { pending: PendingRouteKey };

export interface ViewDef<R> {
  key: string; // C-00 总表页签键
  source: Source;
  echo: string; // 请求串回显端点（原型 mkReq 口径，如 '/admin/card-products'）
  cols: readonly ColDef[];
  cells: (ctx: AdminCtx, r: R) => readonly CellVals[];
  open?: (ctx: AdminCtx, r: R) => void;
  rowActions?: (ctx: AdminCtx, r: R) => readonly ActionDef<R>[];
  stats?: (ctx: AdminCtx, page: AdminPage<R> & Record<string, unknown>) => readonly StatVals[];
  primary?: { label: string; icon: '↻ ' | '+ '; need: Need; run: (ctx: AdminCtx) => void | Promise<void> };
  secondary?: { label: string; icon: '↻ ' | '+ '; need: Need; run: (ctx: AdminCtx) => void | Promise<void> }; // C-27 预算池双主操作（同步 + 新建）
  note?: (t: Dict) => string;
  csvName: string; // 文件名回落段（TD-821）
  noDate?: boolean; // 带 start_date / end_date → 4220 的资源（C-00 总表）
  noKeyword?: boolean;
  noExport?: boolean; // 列表端点不接 format=csv 的资源（06 C-15 Bot 列表）：导出按钮恒隐藏，与 export 位无关
}

// 注册表只保存擦除行类型后的视图；唯一类型断言集中在 defineView（行数据来自同一视图的 fetch，形状由该视图声明）
export interface ErasedView {
  key: string;
  source: Source;
  echo: string;
  cols: readonly ColDef[];
  csvName: string;
  noDate: boolean;
  noKeyword: boolean;
  noExport: boolean;
  cells: (ctx: AdminCtx, r: unknown) => readonly CellVals[];
  open?: (ctx: AdminCtx, r: unknown) => void;
  rowActions?: (ctx: AdminCtx, r: unknown) => readonly ActionDef<unknown>[];
  stats?: (ctx: AdminCtx, page: AdminPage<unknown> & Record<string, unknown>) => readonly StatVals[];
  primary?: ViewDef<unknown>['primary'];
  secondary?: ViewDef<unknown>['secondary'];
  note?: (t: Dict) => string;
}

export function defineView<R>(v: ViewDef<R>): ErasedView {
  const asR = (r: unknown): R => r as R;
  const asPage = (p: AdminPage<unknown> & Record<string, unknown>): AdminPage<R> & Record<string, unknown> => p as AdminPage<R> & Record<string, unknown>;
  return {
    key: v.key,
    source: v.source,
    echo: v.echo,
    cols: v.cols,
    csvName: v.csvName,
    noDate: !!v.noDate,
    noKeyword: !!v.noKeyword,
    noExport: !!v.noExport,
    cells: (ctx, r) => v.cells(ctx, asR(r)),
    ...(v.open ? { open: (ctx: AdminCtx, r: unknown) => v.open?.(ctx, asR(r)) } : {}),
    ...(v.rowActions
      ? {
          rowActions: (ctx: AdminCtx, r: unknown) =>
            (v.rowActions?.(ctx, asR(r)) ?? []).map((a) => ({
              ...a,
              show: a.show ? (x: unknown) => a.show?.(asR(x)) ?? true : undefined,
              gate: a.gate ? (c: AdminCtx, x: unknown) => a.gate?.(c, asR(x)) ?? { disabled: false, title: '' } : undefined,
              run: (c: AdminCtx, x: unknown) => a.run(c, asR(x)),
            })),
        }
      : {}),
    ...(v.stats ? { stats: (ctx: AdminCtx, p: AdminPage<unknown> & Record<string, unknown>) => v.stats?.(ctx, asPage(p)) ?? [] } : {}),
    ...(v.primary ? { primary: v.primary } : {}),
    ...(v.secondary ? { secondary: v.secondary } : {}),
    ...(v.note ? { note: v.note } : {}),
  };
}

export interface PageDef {
  page: Page;
  tabLabels: (t: Dict) => readonly string[]; // 页签文案（原型 tabsFor 或改写 / 新增键）
  views: readonly ErasedView[]; // 与页签一一对应
  // TD-849：本页文案包。table 域以 mergePagePacks 并入字典；同一页文件内多个 PageDef 共享同一对象引用（去重）
  i18n?: I18nPack;
  rewrites?: readonly string[]; // 覆盖原型键（06 [改写 key]）
}

export type PageMap = Readonly<Partial<Record<Page, PageDef>>>;

export function chipMap(t: Dict, entries: Readonly<Record<string, readonly [ChipKind, string]>>): Readonly<Record<string, readonly [ChipKind, string]>> {
  return Object.fromEntries(
    Object.entries(entries).map(([k, [kind, key]]) => {
      const v = t[key];
      return [k, [kind, typeof v === 'string' ? v : key] as const];
    }),
  );
}
